import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { Link as LinkRouter, Redirect, useRouter } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AwarenessLogo } from '@/components/AwarenessLogo';
import { Header } from '@/components/header';
import PromotionalCarousel from '@/components/promotional-carousel';
import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Link, LinkText } from '@/components/ui/link';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useAuth } from '@/hooks/useAuth';
import { useColors } from '@/hooks/useColors';
import { useCreateAccount } from '@/hooks/useCreateAccount';
import { useRedirectPath } from '@/hooks/useRedirectPath';
import { authenticatedUserIdAtom, userIdAtom } from '@/services/globalState';
import { trackCreateAccountCtaTriggered } from '@/util/analyticsEvents';

let AUTO_LOGIN = false;

let AUTO_LOGIN_EMAIL = '';

let AUTO_LOGIN_PASSWORD = '';

if (__DEV__) {
  try {
    const autoLoginFile = require('../../.auto-login.json');
    AUTO_LOGIN = autoLoginFile?.AUTO_LOGIN;

    AUTO_LOGIN_EMAIL = autoLoginFile?.AUTO_LOGIN_EMAIL;

    AUTO_LOGIN_PASSWORD = autoLoginFile?.AUTO_LOGIN_PASSWORD;

    // TODO: Remove auto-login behavior entirely
  } catch {}
}

/**
 * Landing page component that shows onboarding carousel on mobile and redirects to Directory on web.
 * On web, landing page is the public Directory.
 */
export default function Index() {
  const { colors } = useColors();

  const { login, getCurrentAuthUser } = useAuth();

  const userId = useAtomValue(userIdAtom);

  const setUserId = useSetAtom(authenticatedUserIdAtom);

  const { reset } = useCreateAccount();

  /*
   * A visitor can arrive here still carrying the destination a link named. Both calls to
   * action have to forward it, or the destination survives the auth gate only to be
   * dropped by the screen that offers the way through it.
   */
  const { appendRedirectPath } = useRedirectPath();

  const { navigate } = useRouter();

  useEffect(() => {
    if (__DEV__ && AUTO_LOGIN && AUTO_LOGIN_EMAIL && AUTO_LOGIN_PASSWORD) {
      (async () => {
        const currentAuthUser = await getCurrentAuthUser();

        if (!currentAuthUser && !userId) {
          login({
            email: AUTO_LOGIN_EMAIL,
            password: AUTO_LOGIN_PASSWORD,
          });
        } else if (currentAuthUser && !userId) {
          setUserId(userId);
        }
      })();
    }
  }, []);

  if (isWeb) {
    return <Redirect href="/(open)/(sections)/directory" />;
  }

  return (
    <SafeAreaView className="flex-1 flex-shrink flex-col items-center justify-between pb-8">
      <AwarenessLogo
        alt="Primum logo with mark"
        kind="dark"
        box={{ height: 28, width: 224, marginVertical: 32 }}
      />
      <Header headerShown={false} statusBarBackgroundColor={colors.background[0]} />

      <VStack className="flex-1 items-center justify-center">
        <PromotionalCarousel />
      </VStack>

      <VStack className="w-full justify-end px-6">
        <LinkRouter href={appendRedirectPath('/login')} asChild>
          <Button testID="landing-sign-in-button">
            <ButtonText>Sign in</ButtonText>
          </Button>
        </LinkRouter>

        <HStack className="mt-4 justify-center gap-1">
          <Text>Not a member? </Text>

          <Link
            onPress={() => {
              reset();

              trackCreateAccountCtaTriggered({ cta: 'join_primum', source: 'landing_page' });

              navigate(appendRedirectPath('/create-account/step-1'));
            }}>
            <LinkText className="text-primary-500 data-[hover=true]:text-primary-600">
              Join Primum{' '}
            </LinkText>
          </Link>
        </HStack>
      </VStack>
    </SafeAreaView>
  );
}
